import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Check, Loader2 } from 'lucide-react';

// Newsletter categories
const newsletterCategories = [
    { label: 'Funding Announcements', value: 'funding' },
    { label: 'Blog Posts', value: 'blog' },
    { label: 'Podcast Episodes', value: 'podcast' },
    { label: 'Market Research', value: 'research' },
    { label: 'Events', value: 'events' },
    { label: 'PSHG Hiring', value: 'hiring' },
    { label: 'Portfolio Companies Hiring', value: 'portfolio_hiring' },
];

export function NewsletterSubscription({ className = '' }) {
    const [email, setEmail] = useState('');
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
    const [honeypot, setHoneypot] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [error, setError] = useState('');

    const handleCategoryChange = (value: string) => {
        setSelectedCategories(prev =>
            prev.includes(value)
                ? prev.filter(cat => cat !== value)
                : [...prev, value]
        );
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        // Check for honeypot
        if (honeypot) {
            // If honeypot is filled, silently reject but appear to accept
            setIsSuccess(true);
            return;
        }

        if (!email) {
            setError('Please enter your email address');
            return;
        }

        setIsSubmitting(true);
        setError('');

        try {
            // Simulate API call - in production, you would make an actual API call
            await new Promise(r => setTimeout(r, 800));

            // Reset form and show success message
            setIsSuccess(true);
            setEmail('');
            setSelectedCategories([]);
        } catch (err) {
            setError('Something went wrong. Please try again.');
            console.error('Newsletter submission error:', err);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isSuccess) {
        return (
            <div className={`${className} bg-primary/5 rounded-lg p-6 text-center`}>
                <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                    <Check className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Thank You!</h3>
                <p className="text-muted-foreground mb-4">
                    You've successfully subscribed to our newsletter.
                    Please check your email to confirm your subscription.
                </p>
                <Button
                    variant="outline"
                    onClick={() => setIsSuccess(false)}
                    size="sm"
                >
                    Subscribe Another Email
                </Button>
            </div>
        );
    }

    return (
        <div className={`${className}`}>
            <form onSubmit={handleSubmit} className="space-y-4">
                {/* Honeypot field - hidden from users but visible to bots */}
                <div className="absolute opacity-0 pointer-events-none" aria-hidden="true">
                    <label htmlFor="website">Website</label>
                    <input
                        type="text"
                        id="website"
                        name="website"
                        value={honeypot}
                        onChange={(e) => setHoneypot(e.target.value)}
                        tabIndex={-1}
                        autoComplete="off"
                    />
                </div>

                <div>
                    <h3 className="text-lg font-semibold mb-2">Subscribe to Our Newsletter</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                        Stay updated with our latest insights, investment opportunities, and company news.
                    </p>
                </div>

                <div className="space-y-3">
                    <div>
                        <label htmlFor="email" className="sr-only">Email address</label>
                        <input
                            id="email"
                            type="email"
                            placeholder="Your email address"
                            required
                            className="w-full p-2.5 border rounded-md"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                    </div>

                    <div>
                        <p className="text-sm font-medium mb-2">I'm interested in (optional):</p>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                            {newsletterCategories.map(category => (
                                <div key={category.value} className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id={`category-${category.value}`}
                                        className="mr-2 rounded"
                                        checked={selectedCategories.includes(category.value)}
                                        onChange={() => handleCategoryChange(category.value)}
                                    />
                                    <label
                                        htmlFor={`category-${category.value}`}
                                        className="text-sm cursor-pointer"
                                    >
                                        {category.label}
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {error && (
                    <div className="text-red-600 text-sm">{error}</div>
                )}

                <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full"
                >
                    {isSubmitting ? (
                        <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Subscribing...
                        </>
                    ) : (
                        'Subscribe'
                    )}
                </Button>
            </form>
        </div>
    );
} 